import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, ShieldCheck } from 'lucide-react';
import { kycFlowService, otpService } from '../../../services/api';

// /customer-kyc?mobile=… — the old KYC link carried only a mobile number, so anyone
// could open it. It now leads to the secure investor profile (/investor-profile?token=…):
//   • staff signed in to the dashboard go straight there (link created/reused for them);
//   • investors confirm the mobile with a WhatsApp OTP first.

const hasStaffSession = () => {
    try { return !!JSON.parse(window.localStorage.getItem('ak_dashboard_session') || 'null')?.mobile; } catch { return false; }
};

const maskMobile = (m: string) => (m.length >= 4 ? `+91 ••••• •${m.slice(-4)}` : m);

const KycLinkGate: React.FC = () => {
    const [params] = useSearchParams();
    const initialMobile = (params.get('mobile') || '').replace(/\D/g, '').slice(-10);
    const [mobile, setMobile] = useState(initialMobile);
    const [stage, setStage] = useState<'checking' | 'enter' | 'otp'>(hasStaffSession() && initialMobile ? 'checking' : 'enter');
    const [otp, setOtp] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [resendIn, setResendIn] = useState(0);
    const otpRef = useRef<HTMLInputElement>(null);

    const go = (path: string) => window.location.replace(path);

    // Staff: skip the OTP and open the investor's secure profile directly.
    useEffect(() => {
        if (stage !== 'checking') return;
        (async () => {
            const res = await kycFlowService.createLink(initialMobile);
            if (res.error || !res.data?.path) { setStage('enter'); return; }
            go(res.data.path);
        })();
    }, [stage, initialMobile]);

    useEffect(() => {
        if (resendIn <= 0) return;
        const t = setTimeout(() => setResendIn(n => n - 1), 1000);
        return () => clearTimeout(t);
    }, [resendIn]);

    const sendOtp = async () => {
        if (!/^[6-9]\d{9}$/.test(mobile)) { setError('Enter your 10-digit mobile number'); return; }
        setBusy(true); setError(null);
        const res = await otpService.sendOtp(mobile, 'animalkart');
        setBusy(false);
        const body = res.data;
        if (res.error || (body && body.statuscode && body.statuscode !== 200)) {
            setError(res.error || body?.message || 'Could not send the OTP. Please try again.');
            return;
        }
        setStage('otp'); setOtp(''); setResendIn(30);
        setTimeout(() => otpRef.current?.focus(), 50);
    };

    const verify = async () => {
        if (!/^\d{4,6}$/.test(otp)) { setError('Enter the OTP we sent on WhatsApp'); return; }
        setBusy(true); setError(null);
        const res = await kycFlowService.otpLink(mobile, otp);
        setBusy(false);
        if (res.error || !res.data?.path) { setError(res.error || 'Could not verify the OTP'); return; }
        go(res.data.path);
    };

    if (stage === 'checking') {
        return <div className="min-h-screen grid place-items-center bg-[#F3F6F4]"><Loader2 className="animate-spin text-[#1B4332]" /></div>;
    }

    return (
        <div className="min-h-screen bg-[#F3F6F4] text-[#14231B] flex flex-col">
            <header className="bg-[#163829] text-white">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-2.5 font-extrabold">
                    <span className="w-8 h-8 rounded-lg bg-[#D8F3DC] text-[#1B4332] grid place-items-center"><ShieldCheck size={17} /></span>
                    Markwave
                </div>
            </header>
            <main className="flex-1 grid place-items-center px-4 py-10">
                <div className="w-full max-w-md">
                    <div className="text-[11px] font-bold tracking-[.08em] uppercase text-[#4A6656]">Investor profile</div>
                    <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight mt-1">Confirm it's you</h1>
                    <p className="text-sm text-[#4A6656] mt-2">
                        Your KYC, bank and nominee details are private. We'll send a one-time code on WhatsApp before opening them.
                    </p>

                    <form className="mt-6 bg-white rounded-2xl ring-1 ring-[#DDE5E0] p-5 sm:p-6 flex flex-col gap-4"
                        onSubmit={e => { e.preventDefault(); stage === 'otp' ? verify() : sendOtp(); }}>
                        {stage === 'enter' ? (
                            <div>
                                <label htmlFor="kyc-mobile" className="block text-[13px] font-bold text-slate-700 mb-1.5">Mobile number</label>
                                <div className="flex">
                                    <span className="h-12 px-3 grid place-items-center rounded-l-xl border border-r-0 border-[#C5D4CB] bg-[#F7F9F8] text-sm font-semibold text-slate-600">+91</span>
                                    <input id="kyc-mobile" inputMode="numeric" autoComplete="tel-national" value={mobile} placeholder="10-digit mobile"
                                        onChange={e => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                        className="flex-1 min-w-0 h-12 px-3 rounded-r-xl border border-[#C5D4CB] text-base font-mono outline-none focus:border-[#1B4332] focus:ring-2 focus:ring-[#1B4332]/15" />
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div className="flex items-center justify-between gap-2 mb-1.5">
                                    <label htmlFor="kyc-otp" className="text-[13px] font-bold text-slate-700">OTP sent to {maskMobile(mobile)}</label>
                                    <button type="button" onClick={() => { setStage('enter'); setError(null); }} className="text-xs font-bold text-[#1B4332] underline">Change</button>
                                </div>
                                <input id="kyc-otp" ref={otpRef} inputMode="numeric" autoComplete="one-time-code" value={otp} placeholder="••••••"
                                    onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    className="w-full h-12 px-3 rounded-xl border border-[#C5D4CB] text-xl tracking-[.4em] text-center font-mono outline-none focus:border-[#1B4332] focus:ring-2 focus:ring-[#1B4332]/15" />
                                <div className="mt-2 text-xs text-[#4A6656]">
                                    {resendIn > 0 ? `Resend in ${resendIn}s` : <button type="button" disabled={busy} onClick={sendOtp} className="font-bold text-[#1B4332] underline">Resend OTP</button>}
                                </div>
                            </div>
                        )}
                        {error && <p role="alert" className="text-sm font-semibold text-red-700">{error}</p>}
                        <button type="submit" disabled={busy}
                            className="h-12 rounded-xl bg-[#1B4332] text-white text-sm font-bold inline-flex items-center justify-center gap-2 hover:bg-[#12301F] disabled:opacity-50">
                            {busy && <Loader2 size={16} className="animate-spin" />}
                            {stage === 'otp' ? 'Verify & open my profile' : 'Send OTP on WhatsApp'}
                        </button>
                    </form>
                    <p className="text-xs text-[#4A6656] mt-4 text-center">Trouble getting the code? Contact Markwave support.</p>
                </div>
            </main>
        </div>
    );
};

export default KycLinkGate;
